import { $generateHtmlFromNodes, initialPrefillTag, RichTextEditor } from '@talentmesh/rte';
import React, { useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useNotificationContext } from '@/Context/NotificationContext';
import useDebounce from '@/Hooks/UseDebounce';
import ProcessingState from '@/Models/ProcessingState';
import UIStrings from '@/Utils/UIStrings';
import { Label } from '@/Components/shadcn/label';
import { JobAdFieldTypes, JobAdFormValues } from '../JobAdStepUtils';
import DescriptionWings from './DescriptionWings.svg';
import JobDescriptionCharCounter from './JobDescriptionCharCounter';
import RTELoadingOverlay from './RTELoadingOverlay';

interface JobDescriptionProps {
    initialHtml: string;
    processingState: ProcessingState;
}

function JobDescription({ initialHtml, processingState }: JobDescriptionProps) {
    const { showFailToaster } = useNotificationContext();
    const { formState, setValue } = useFormContext<JobAdFormValues>();
    const { isSubmitting, errors } = formState;
    const [jobDescription, setJobDescription] = useState({
        value: '',
        textLength: 0,
        initialized: false,
        isPrefillValue: true,
    });
    const debouncedJobDescription = useDebounce(jobDescription, 10);
    const loading = useMemo(() => processingState === ProcessingState.Processing, [processingState]);
    const shouldValidateLen = useMemo(
        () => !loading && jobDescription.initialized,
        [loading, jobDescription.initialized],
    );

    // actually propagate job description to form state mngr here
    // for performance purposes
    useEffect(() => {
        setJobDescription((p) => ({ ...p, initialized: true }));

        const isPrefill = jobDescription.isPrefillValue;
        setValue('description', debouncedJobDescription.value, { shouldDirty: !isPrefill });
        setValue('descriptionLen', debouncedJobDescription.textLength, { shouldValidate: shouldValidateLen });
    }, [debouncedJobDescription]);

    return (
        <div className="flex flex-col gap-2.5">
            <Label>{UIStrings.JobDescription}</Label>
            <div className="flex h-172 flex-col" data-cy="JobDescriptionRTE">
                <RichTextEditor
                    editable={!isSubmitting}
                    loading={loading}
                    initialHtml={initialHtml}
                    loadingOverlay={
                        <RTELoadingOverlay dataCy="JobAdDescriptionLoadingOverlay" svg={DescriptionWings} />
                    }
                    data-cy={JobAdFieldTypes.Description}
                    onError={() => {
                        showFailToaster();
                    }}
                    features={['headingStyle', 'bold', 'italic', 'ol', 'ul']}
                    onChange={(editorState, editor, tags) => {
                        editorState.read(() => {
                            const textLength = editor.getRootElement()?.textContent?.length || 0;
                            const value = $generateHtmlFromNodes(editor);

                            setJobDescription((p) => ({
                                ...p,
                                value,
                                textLength,
                                isPrefillValue: tags.has(initialPrefillTag),
                            }));
                        });
                    }}
                    toolbarProps={{
                        className: 'bg-card',
                    }}
                    contentEditableProps={{
                        className: `overflow-y-auto flex-1 ${isSubmitting ? 'text-muted-foreground' : ''}`,
                    }}
                />
            </div>
            <JobDescriptionCharCounter
                hasError={!!errors.descriptionLen?.message}
                error={errors.descriptionLen?.message}
            />
        </div>
    );
}

export default JobDescription;
